/**
 * Shared "page hero + filter row" components.
 *
 * Used by Marketplace browse, Workflows page, and any future workspace page
 * that needs an oriented hero strip + search/segment/dropdown filter row.
 *
 * - <PageHero/> renders the eyebrow / title / subtitle / pill-style stats.
 * - <PageFilterRow/> is a flex container that hosts <PageSearch/>,
 *   <PageSegment/>, <PageDropdown/> and any custom right-aligned slots.
 *
 * Click-outside is handled by <PageDropdown/> internally, so consumers
 * only pass the trigger contents and the items.
 */
import clsx from 'clsx';
import type { CSSProperties, ReactElement, ReactNode, RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { SearchIcon } from '../../../dialogs/marketplace/icons';
import * as styles from './page-hero-filter.css';
import { usePageChromeCollapse } from './page-shell';

// =============================================================================
// Shared header surface
// =============================================================================

export function PageHeaderGroup({
  children,
}: {
  children: ReactNode;
}): ReactElement {
  return <section className={styles.pageHeaderGroup}>{children}</section>;
}

// =============================================================================
// Hero
// =============================================================================

export interface PageHeroStat {
  label: string;
  value: string;
}

export interface PageHeroProps {
  eyebrow: string;
  title: string;
  subtitle: string;
  stats?: PageHeroStat[];
  children?: ReactNode;
}

export function PageHero({
  eyebrow,
  title,
  subtitle,
  stats,
  children,
}: PageHeroProps): ReactElement {
  const chromeCollapse = usePageChromeCollapse();
  const collapseProgress = chromeCollapse?.heroCollapseProgress ?? 0;

  return (
    <header
      className={styles.pageHero}
      data-chrome-hidden={collapseProgress > 0.98}
      style={
        {
          '--sl-page-hero-collapse': collapseProgress,
        } as CSSProperties
      }
    >
      <p className={styles.pageHeroEyebrow}>{eyebrow}</p>
      <h1 className={styles.pageHeroTitle}>{title}</h1>
      <p className={styles.pageHeroSubtitle}>{subtitle}</p>
      {stats && stats.length > 0 ? (
        <div className={styles.pageHeroStats}>
          {stats.map(stat => (
            <span key={stat.label} className={styles.pageHeroStat}>
              <span className={styles.pageHeroStatValue}>{stat.value}</span>
              {stat.label}
            </span>
          ))}
        </div>
      ) : null}
      {children ? <div className={styles.pageHeroExtra}>{children}</div> : null}
    </header>
  );
}

// =============================================================================
// Filter row
// =============================================================================

export function PageFilterRow({
  children,
}: {
  children: ReactNode;
}): ReactElement {
  const chromeCollapse = usePageChromeCollapse();
  const collapseProgress = chromeCollapse?.filterCollapseProgress ?? 0;

  return (
    <div
      className={styles.pageFilterRow}
      data-chrome-hidden={collapseProgress > 0.98}
      style={
        {
          '--sl-page-filter-collapse': collapseProgress,
        } as CSSProperties
      }
    >
      {children}
    </div>
  );
}

// Search ----------------------------------------------------------------------

export interface PageSearchProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}

export function PageSearch({
  value,
  onChange,
  placeholder,
}: PageSearchProps): ReactElement {
  return (
    <div className={styles.pageSearchWrap}>
      <span className={styles.pageSearchIcon}>
        <SearchIcon size={14} />
      </span>
      <input
        type="text"
        className={styles.pageSearchInput}
        placeholder={placeholder}
        value={value}
        onChange={e => onChange(e.target.value)}
      />
    </div>
  );
}

export function PageResultCount({
  children,
  testId,
}: {
  children: ReactNode;
  testId?: string;
}): ReactElement {
  return (
    <span
      className={styles.pageResultCount}
      role="status"
      aria-live="polite"
      data-testid={testId}
    >
      {children}
    </span>
  );
}

// Segmented control -----------------------------------------------------------

export interface PageSegmentOption<K extends string> {
  key: K;
  label: ReactNode;
  ariaLabel?: string;
}

export interface PageSegmentProps<K extends string> {
  options: ReadonlyArray<PageSegmentOption<K>>;
  active: K;
  onChange: (key: K) => void;
  ariaLabel?: string;
}

export function PageSegment<K extends string>({
  options,
  active,
  onChange,
  ariaLabel,
}: PageSegmentProps<K>): ReactElement {
  const activeOption = options.find(opt => opt.key === active) ?? options[0];

  return (
    <>
      <div className={styles.pageSegment} role="tablist" aria-label={ariaLabel}>
        {options.map(opt => (
          <button
            key={opt.key}
            type="button"
            role="tab"
            aria-selected={active === opt.key}
            aria-label={opt.ariaLabel}
            className={clsx(
              styles.pageSegmentButton,
              active === opt.key && styles.pageSegmentButtonActive
            )}
            onClick={() => onChange(opt.key)}
          >
            {opt.label}
          </button>
        ))}
      </div>
      <div className={styles.pageSegmentMobile}>
        <PageDropdown
          ariaLabel={ariaLabel}
          trigger={
            <>
              {activeOption?.label}
              <span className={styles.pageDropdownCaret}>▾</span>
            </>
          }
        >
          {({ close }) =>
            options.map(opt => (
              <PageDropdownItem
                key={opt.key}
                selected={active === opt.key}
                onClick={() => {
                  onChange(opt.key);
                  close();
                }}
              >
                {opt.label}
              </PageDropdownItem>
            ))
          }
        </PageDropdown>
      </div>
    </>
  );
}

// Dropdown --------------------------------------------------------------------

export interface PageDropdownProps {
  /** Element rendered inside the trigger button. */
  trigger: ReactNode;
  /** When true, the trigger renders in the "active" tinted style. */
  active?: boolean;
  /** Menu contents. Open state is managed by PageDropdown. */
  children: (api: { close: () => void }) => ReactNode;
  /** Optional ref to expose the anchor element to the parent. */
  anchorRef?: RefObject<HTMLDivElement | null>;
  ariaLabel?: string;
}

export function PageDropdown({
  trigger,
  active,
  children,
  anchorRef,
  ariaLabel,
}: PageDropdownProps): ReactElement {
  const [open, setOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{
    left: number;
    top: number;
    width: number;
    openUp: boolean;
  } | null>(null);
  const internalRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const ref = anchorRef ?? internalRef;

  const updateMenuPosition = useCallback(() => {
    const anchor = ref.current;
    if (!anchor || typeof window === 'undefined') return;

    const rect = anchor.getBoundingClientRect();
    const viewportPadding = 12;
    const preferredWidth = Math.max(220, rect.width);
    const maxWidth = Math.max(160, window.innerWidth - viewportPadding * 2);
    const width = Math.min(preferredWidth, maxWidth);
    const left = Math.max(
      viewportPadding,
      Math.min(rect.right - width, window.innerWidth - width - viewportPadding)
    );
    const menuMaxHeight = 280;
    const gap = 6;
    const openUp =
      rect.bottom + gap + menuMaxHeight > window.innerHeight &&
      rect.top > menuMaxHeight + gap;

    setMenuPosition({
      left,
      top: openUp ? rect.top - gap : rect.bottom + gap,
      width,
      openUp,
    });
  }, [ref]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        !ref.current?.contains(target) &&
        !menuRef.current?.contains(target)
      ) {
        setOpen(false);
      }
    };
    const onReposition = () => updateMenuPosition();
    updateMenuPosition();
    document.addEventListener('mousedown', onDocClick);
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, ref, updateMenuPosition]);

  return (
    <div className={styles.pageDropdownAnchor} ref={ref}>
      <button
        type="button"
        className={clsx(
          styles.pageDropdownTrigger,
          active && styles.pageDropdownTriggerActive
        )}
        onClick={() => {
          const next = !open;
          if (next) updateMenuPosition();
          setOpen(next);
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
      >
        {trigger}
      </button>
      {open &&
        menuPosition &&
        createPortal(
          <div
            ref={menuRef}
            className={styles.pageDropdownMenu}
            role="listbox"
            style={{
              left: menuPosition.left,
              top: menuPosition.top,
              width: menuPosition.width,
              transform: menuPosition.openUp ? 'translateY(-100%)' : undefined,
            }}
          >
            {children({ close: () => setOpen(false) })}
          </div>,
          document.body
        )}
    </div>
  );
}

export interface PageDropdownItemProps {
  selected?: boolean;
  onClick: () => void;
  children: ReactNode;
}

export function PageDropdownItem({
  selected,
  onClick,
  children,
}: PageDropdownItemProps): ReactElement {
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      className={clsx(
        styles.pageDropdownItem,
        selected && styles.pageDropdownItemActive
      )}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

export function PageDropdownEmpty({
  children,
}: {
  children: ReactNode;
}): ReactElement {
  return <div className={styles.pageDropdownEmpty}>{children}</div>;
}

export function PageDropdownLabel({
  children,
}: {
  children: ReactNode;
}): ReactElement {
  return <span className={styles.pageDropdownLabel}>{children}</span>;
}
